import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaHeart,
  FaEnvelope,
  FaLock,
  FaGlobe,
  FaKey,
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaUser,
  FaShieldAlt,
} from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
const Login = () => {
  const [view, setView] = useState("login");
  const [loginMethod, setLoginMethod] = useState("password");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [otpSent, setOtpSent] = useState(false);

  const [loadingSendOtp, setLoadingSendOtp] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);

  const [errors, setErrors] = useState({});

  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const switchView = (newView) => {
    setView(newView);
    setOtpSent(false);
    setOtp("");
    setPassword("");
    setNewPassword("");
    setErrors({});
  };

  const handleSendOtp = async () => {
    if (!identifier.trim()) return;
    setLoadingSendOtp(true);
    setErrors({});
    try {
      // Determine if email or mobile
      const isEmail = identifier.includes("@");
      const body = {
        [isEmail ? "email" : "mobile"]: identifier,
        type: view === "login" ? "login" : "forgot", // crucial: type depends on current view
      };

      await api.post("/send-otp", body);
      setOtpSent(true);
    } catch (error) {
      setErrors({
        identifier: error.response?.data?.message || "Failed to send OTP",
      });
    } finally {
      setLoadingSendOtp(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoadingLogin(true);
    setErrors({});
    try {
      let response;
      if (loginMethod === "password") {
        const isEmail = identifier.includes("@");
        response = await api.post("/login", {
          [isEmail ? "email" : "mobile"]: identifier,
          password,
        });
      } else {
        // OTP Login → use verify-otp with type "login"
        const isEmail = identifier.includes("@");
        response = await api.post("/verify-otp", {
          [isEmail ? "email" : "mobile"]: identifier,
          otp,
          type: "login",
        });
      }

      const data = response.data;
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      toast.success("Login Successful! Welcome back ❤️", {
        style: {
          borderRadius: "12px",
          background: "linear-gradient(to right, #f43f5e, #ec4899)",
          color: "#fff",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#ffffff",
          secondary: "#f43f5e",
        },
      });

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (error) {
      setErrors({
        identifier: error.response?.data?.message || "Login failed",
      });
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoadingReset(true);
    setErrors({});
    try {
      const isEmail = identifier.includes("@");
      await api.post("/reset-password", {
        [isEmail ? "email" : "mobile"]: identifier,
        otp,
        newPassword,
      });
      switchView("login");
      setLoginMethod("password");
    } catch (error) {
      setErrors({
        otp: error.response?.data?.message || "Failed to reset password",
      });
    } finally {
      setLoadingReset(false);
    }
  };
  const t = {
    English: {
      welcome: "Welcome Back",
      pleaseLogin: "Please login to continue",
      emailOrMobile: "Email or Mobile Number",
      password: "Password",
      otp: "Enter OTP",
      newPassword: "New Password",
      login: "Login",
      sendOtp: "Send OTP",
      verifyLogin: "Verify & Login",
      resetPassword: "Reset Password",
      forgot: "Forgot Password?",
      loginWithOtp: "Or login with OTP",
      noAccount: "Don't have an account?",
      register: "Register Free",
      backToLogin: "Back to Login",
      resetTitle: "Reset Your Password",
      loading: "Loading...",
      tagline: "Where connections blossom",
    },
    Tamil: {
      welcome: "மீண்டும் வருக",
      pleaseLogin: "தொடர உள்நுழையவும்",
      emailOrMobile: "மின்னஞ்சல் அல்லது அலைபேசி எண்",
      password: "கடவுச்சொல்",
      otp: "OTP ஐ உள்ளிடவும்",
      newPassword: "புதிய கடவுச்சொல்",
      login: "உள்நுழைய",
      sendOtp: "OTP அனுப்பு",
      verifyLogin: "சரிபார்த்து உள்நுழையவும்",
      resetPassword: "கடவுச்சொல்லை மீட்டமைக்கவும்",
      forgot: "கடவுச்சொல் மறந்ததா?",
      loginWithOtp: "அல்லது OTP மூலம் உள்நுழைய",
      noAccount: "கணக்கு இல்லையா?",
      register: "இலவச பதிவு",
      backToLogin: "உள்நுழைவுக்கு திரும்புக",
      resetTitle: "கடவுச்சொல்லை மீட்டமைக்கவும்",
      loading: "ஏற்றுகிறது...",
      tagline: "இணைப்புகள் மலரும் இடம்",
    },
  };

  const txt = t[language];

  const isIdentifierFilled = identifier.trim().length > 0;
  const isPasswordFilled = password.trim().length > 0;
  const isOtpFilled = otp.trim().length === 4;
  const isNewPasswordFilled = newPassword.trim().length >= 4;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center relative overflow-hidden">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
        }}
      />
      {/* Language Toggle */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition"
        >
          <FaGlobe className="text-gray-600" />
          <span className="font-medium text-gray-700">
            {language === "English" ? "தமிழ்" : "English"}
          </span>
        </button>
      </div>

      {/* Side-by-side Layout */}
      <div className="w-full max-w-full mx-auto grid lg:grid-cols-2 grid-cols-1 h-screen">
        {/* Left Side - Image & Branding */}
        <div className="hidden lg:flex flex-col justify-center items-center bg-gradient-to-br from-rose-500 to-pink-600 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative z-10 text-center px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="p-8 bg-white bg-opacity-10 backdrop-blur-sm rounded-full mb-8 inline-block">
                <img
                  src="/logo.png" // path to your logo
                  alt="Logo"
                  className="h-20 w-full object-contain" // adjust size as needed
                />
              </div>

              <p className="text-2xl opacity-90">{txt.tagline}</p>
              <p className="mt-8 text-lg opacity-80 max-w-md mx-auto">
                Find meaningful connections in a safe and trusted environment.
              </p>
            </motion.div>
          </div>
          {/* Decorative hearts */}
          <div className="absolute bottom-10 left-10 text-pink-300 opacity-30 text-9xl">
            <FaHeart />
          </div>
          <div className="absolute top-20 right-10 text-pink-300 opacity-30 text-7xl rotate-12">
            <FaHeart />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex items-center justify-center px-6 py-12 lg:py-0">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <div className="rounded-2xl">
              {/* Mobile-only Branding */}
              <div className="lg:hidden text-center mb-8">
                <div className="p-5 bg-rose-100 rounded-full inline-block">
                  <FaHeart className="text-5xl text-rose-600" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 mt-4">
                  ST <span className="text-rose-600">MatchZone</span>
                </h1>
                <p className="text-gray-600">{txt.tagline}</p>
              </div>

              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900">
                  {view === "login" ? txt.welcome : txt.resetTitle}
                </h2>
                <p className="text-gray-600 mt-2">
                  {view === "login"
                    ? txt.pleaseLogin
                    : "Securely reset your password"}
                </p>
              </div>

              {view === "login" ? (
                <form onSubmit={handleLogin} className="space-y-6">
                  <div className="relative">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition"
                      placeholder={txt.emailOrMobile}
                      required
                    />
                    {errors.identifier && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.identifier}
                      </p>
                    )}
                  </div>

                  {loginMethod === "password" ? (
                    <div className="relative">
                      <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition"
                        placeholder={txt.password}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {errors.password && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.password}
                        </p>
                      )}
                    </div>
                  ) : !otpSent ? null : (
                    <div className="relative">
                      <FaKey className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.slice(0, 4))}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition text-center tracking-wider text-lg"
                        placeholder="••••"
                        maxLength="4"
                      />
                      {errors.otp && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.otp}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-4">
                    {loginMethod === "password" ? (
                      <>
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => switchView("forgot-password")}
                            className="text-sm text-rose-600 hover:text-rose-700 font-medium"
                          >
                            {txt.forgot}
                          </button>
                        </div>
                        <div className="flex justify-center">
                          <button
                            type="submit"
                            disabled={
                              !isIdentifierFilled ||
                              !isPasswordFilled ||
                              loadingLogin
                            }
                            className="w-1/2 sm:w-1/3 md:w-1/4 py-4 bg-rose-600 text-white font-bold rounded-lg shadow-md hover:bg-rose-700 transition disabled:opacity-60"
                          >
                            {loadingLogin ? txt.loading : txt.login}
                          </button>
                        </div>
                      </>
                    ) : !otpSent ? (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={!isIdentifierFilled || loadingSendOtp}
                          className="w-auto py-4 px-6 bg-rose-600 text-white font-bold rounded-lg shadow-md hover:bg-rose-700 transition disabled:opacity-60"
                        >
                          {loadingSendOtp ? txt.loading : txt.sendOtp}
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <button
                          type="submit"
                          disabled={!isOtpFilled || loadingLogin}
                          className="w-64 py-4 bg-rose-600 text-white font-bold rounded-lg shadow-md hover:bg-rose-700 transition disabled:opacity-60"
                        >
                          {loadingLogin ? txt.loading : txt.verifyLogin}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMethod(
                          loginMethod === "password" ? "otp" : "password"
                        );
                        setOtpSent(false);
                        setErrors({});
                      }}
                      className="text-sm text-gray-600 hover:text-rose-600"
                    >
                      {loginMethod === "password"
                        ? txt.loginWithOtp
                        : "Use password instead"}
                    </button>
                  </div>
                </form>
              ) : (
                /* Forgot Password Form */
                <form onSubmit={handleResetPassword} className="space-y-6">
                  <button
                    type="button"
                    onClick={() => switchView("login")}
                    className="flex items-center gap-2 text-gray-600 hover:text-rose-600 text-sm font-medium"
                  >
                    <FaArrowLeft /> {txt.backToLogin}
                  </button>

                  <div className="space-y-6">
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition"
                        placeholder={txt.emailOrMobile}
                        disabled={otpSent}
                        required
                      />
                      {errors.identifier && (
                        <p className="mt-2 text-sm text-red-600">
                          {errors.identifier}
                        </p>
                      )}
                    </div>

                    {!otpSent ? (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={!isIdentifierFilled || loadingSendOtp}
                          className="w-1/2 sm:w-1/3 md:w-1/4 py-4 bg-rose-600 text-white font-bold rounded-lg shadow-md hover:bg-rose-700 transition disabled:opacity-60"
                        >
                          {loadingSendOtp ? txt.loading : txt.sendOtp}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="relative">
                          <FaKey className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                            className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition text-center tracking-wider text-lg"
                            placeholder="••••••"
                            maxLength="6"
                          />
                          {errors.otp && (
                            <p className="mt-2 text-sm text-red-600">
                              {errors.otp}
                            </p>
                          )}
                        </div>

                        <div className="relative">
                          <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-300 rounded-lg focus:border-rose-500 focus:ring-4 focus:ring-rose-100 outline-none transition"
                            placeholder={txt.newPassword}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          {errors.newPassword && (
                            <p className="mt-2 text-sm text-red-600">
                              {errors.newPassword}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-center">
                          <button
                            type="submit"
                            disabled={
                              !isOtpFilled ||
                              !isNewPasswordFilled ||
                              loadingReset
                            }
                            className="w-auto py-4 px-6 bg-rose-600 text-white font-bold rounded-lg shadow-md hover:bg-rose-700 transition disabled:opacity-60"
                          >
                            {loadingReset ? txt.loading : txt.resetPassword}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </form>
              )}

              {/* Features */}
              <div className="mt-8 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                  <FaUser className="text-rose-600 text-xl" />
                  <div>
                    <p className="font-medium text-gray-900">Secure Login</p>
                    <p className="text-xs text-gray-600">Multiple methods</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-4">
                  <FaShieldAlt className="text-rose-600 text-xl" />
                  <div>
                    <p className="font-medium text-gray-900">Protected</p>
                    <p className="text-xs text-gray-600">
                      Data safety guaranteed
                    </p>
                  </div>
                </div>
              </div>

              {/* Register */}
              <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                <p className="text-gray-600 text-sm mb-4">{txt.noAccount}</p>
                <Link
                  to="/register"
                  className="block w-full py-4 bg-rose-500 text-white font-bold rounded-lg hover:bg-rose-800 transition"
                >
                  {txt.register}
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
